import {
  collection, addDoc, updateDoc, doc,
  serverTimestamp, arrayUnion, getDoc,
  query, where, orderBy, onSnapshot, deleteDoc, increment
} from "firebase/firestore";
import { db } from "./firebase";
import { saveNotification } from "./notificationService";

// --- ١. دوال الاشتراك (Subscriptions) المطلوبة للهوكس ---

export function subscribeAllTrips(callback) {
  const q = query(collection(db, "trips"), orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export function subscribeClientTrips(clientId, callback) {
  const q = query(
    collection(db, "trips"),
    where("clientId", "==", clientId),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export function subscribePendingTrips(callback) {
  const q = query(
    collection(db, "trips"),
    where("status", "==", "pending"),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export function subscribeCaptainTrips(captainId, callback) {
  const q = query(
    collection(db, "trips"),
    where("selectedCaptain", "==", captainId),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// --- ٢. دوال العمليات (Actions) مع الإشعارات المصلحة ---

export async function createTrip(tripData) {
  const docRef = await addDoc(collection(db, "trips"), {
    ...tripData,
    status: "pending",
    createdAt: serverTimestamp(),
  });

  await saveNotification("captains_all", {
    title: "طلب رحلة جديد",
    body: `${tripData.from} ← ${tripData.to}`,
    type: "new_trip",
    tripId: docRef.id
  });
  return docRef.id;
}

export async function submitOffer(tripId, offerData) {
  const tripRef = doc(db, "trips", tripId);
  await updateDoc(tripRef, {
    offers: arrayUnion({ ...offerData, createdAt: new Date().toISOString() })
  });

  const tripSnap = await getDoc(tripRef);
  const clientId = tripSnap.data().clientId;

  await saveNotification(clientId, {
    title: "عرض جديد",
    body: `قدم ${offerData.captainName} عرضاً بـ ${offerData.price} ج.م`,
    type: "new_offer",
    tripId
  });
}

export async function selectCaptain(tripId, { captainId, captainName, price, depositAmount }) {
  await updateDoc(doc(db, "trips", tripId), {
    status: "awaiting_deposit",
    selectedCaptain: captainId,
    captainName,
    agreedPrice: price,
    depositAmount,
    captainSelectedAt: serverTimestamp(),
  });

  await saveNotification(captainId, {
    title: "تم اختيارك للرحلة!",
    body: "العميل في مرحلة دفع الديبوزت الآن",
    type: "trip_confirmed",
    tripId
  });
}

export async function submitDepositProof(tripId, url) {
  await updateDoc(doc(db, "trips", tripId), {
    status: "deposit_review",
    transferProofUrl: url,
    transferSubmittedAt: serverTimestamp(),
  });

  await saveNotification("admin", {
    title: "إيصال دفع جديد",
    body: "قام عميل برفع صورة الإيصال للمراجعة",
    type: "deposit_review",
    tripId
  });
}

export async function approveDeposit(tripId) {
  const tripSnap = await getDoc(doc(db, "trips", tripId));
  const data = tripSnap.data();

  await updateDoc(doc(db, "trips", tripId), { status: "confirmed" });

  // إشعار للكابتن والعميل
  await saveNotification(data.selectedCaptain, {
    title: "تم تأكيد الدفع",
    body: "الرحلة الآن مؤكدة، يمكنك التواصل مع العميل",
    type: "trip_confirmed",
    tripId
  });
  await saveNotification(data.clientId, {
    title: "تم قبول الإيصال",
    body: "رحلتك مؤكدة الآن مع الكابتن",
    type: "trip_confirmed",
    tripId
  });
}

export async function rejectDeposit(tripId) {
  const tripSnap = await getDoc(doc(db, "trips", tripId));
  const clientId = tripSnap.data().clientId;

  await updateDoc(doc(db, "trips", tripId), { status: "awaiting_deposit" });

  await saveNotification(clientId, {
    title: "تم رفض الإيصال",
    body: "يرجى التأكد من البيانات وإعادة رفع صورة الإيصال",
    type: "deposit_review",
    tripId
  });
}

export async function captainCompleteTrip(tripId) {
  await updateDoc(doc(db, "trips", tripId), { status: "captain_completed" });

  await saveNotification("admin", {
    title: "طلب إنهاء رحلة",
    body: "أعلن الكابتن عن وصوله وإتمام الرحلة",
    type: "trip_completed",
    tripId
  });
}

export async function completeTrip(tripId, captainId) {
  await updateDoc(doc(db, "trips", tripId), {
    status: "completed",
    completedAt: serverTimestamp()
  });

  // ملاحظة: الرصيد يتم تحديثه عبر Cloud Function (التي كتبناها سابقاً)
  await saveNotification(captainId, {
    title: "تم إكمال الرحلة",
    body: "تم إيداع الأرباح في محفظتك بنجاح",
    type: "trip_completed",
    tripId
  });
}

export async function cancelTrip(tripId) {
  await updateDoc(doc(db, "trips", tripId), { status: "cancelled" });
}

export async function rateTrip(tripId, captainId, rating) {
  await updateDoc(doc(db, "trips", tripId), { rating });

  // تحديث متوسط تقييم الكابتن في مستنده
  const capRef = doc(db, "users", captainId);
  const capSnap = await getDoc(capRef);
  if (capSnap.exists()) {
    const oldRating = capSnap.data().rating || 0;
    const count = capSnap.data().ratingCount || 0;
    const newRating = ((oldRating * count) + rating) / (count + 1);
    await updateDoc(capRef, {
      rating: Number(newRating.toFixed(1)),
      ratingCount: increment(1)
    });
  }
}